import type { AppRouter } from "@dransay/api";
import {
	DELIVERY_METHOD_KIND,
	EXPRESS_COURIER_PROVIDER,
} from "@dransay/api/models";
import type { inferRouterOutputs } from "@trpc/server";

/**
 * Output of `public.getVendorsFiltered` (and of POST /api/vendors-filtered,
 * which returns that procedure's result unchanged).
 */
export type GetVendorsFilteredResult =
	inferRouterOutputs<AppRouter>["public"]["getVendorsFiltered"];

// Runtime includes `distance` when a location was resolved; the CTE type omits it.
export type VendorsFilteredItem = GetVendorsFilteredResult["items"][number] & {
	distance?: number | null;
};

export type VendorsFilteredMetadata = GetVendorsFilteredResult["metadata"];

/**
 * `expressCourierAvailable` is only on the rows when the request carried the
 * express-courier kind (courier plus a known address), so the item type is a
 * union of both shapes. An absent flag reads as "not available", which is the
 * answer a filter needs.
 */
export const isExpressCourierAvailable = (
	vendor: VendorsFilteredItem,
): boolean =>
	"expressCourierAvailable" in vendor &&
	vendor.expressCourierAvailable === true;

/**
 * Uber Direct never receives a Lieferando zone verdict. The vendor query's
 * window and 10 km radius are the verdict for these rows.
 */
export const isUberDirectExpress = (vendor: VendorsFilteredItem): boolean =>
	vendor.deliveryMethods.some(
		(method) =>
			method.kind === DELIVERY_METHOD_KIND.EXPRESS_COURIER &&
			method.provider === EXPRESS_COURIER_PROVIDER.UBER_DIRECT &&
			method.enabled !== false,
	);
